import { getRequest } from "@decibeltrade/sdk";
import { useQuery } from "@tanstack/react-query";
import z from "zod/v4";

import { env } from "@/env";

// Mirror of XchainWithdrawRouteSchema in typescript/server/src/routes/xchain.route.ts — keep the
// field list identical when the server DTO changes.
//
// `chainKind` and `group` are plain strings for the same reason they are on the deposit side:
// enabling a chain or an asset is a server config change, and parsing must not start failing
// because the server learned a value this build has never heard of.
const XchainWithdrawRouteSchema = z.object({
  id: z.string(),
  /** `native` is a plain Aptos transfer; `intents` goes through 1Click and needs a quote. */
  kind: z.string(),
  /** Display asset the route belongs to ("BTC"); every route in a group spends the same token. */
  group: z.string(),
  /** The Aptos-side token being spent. */
  sourceSymbol: z.string(),
  /** The fungible asset both on-chain legs name. */
  sourceFaAddress: z.string(),
  sourceDecimals: z.number(),
  chain: z.string(),
  chainKind: z.string(),
  /** What the user receives. Equal to `sourceSymbol` on every route offered today. */
  destinationSymbol: z.string(),
  /** Smallest withdrawal offered, in display units of the source asset. */
  minAmount: z.string(),
  estimatedSeconds: z.number(),
});

const WithdrawRoutesResponseSchema = z.discriminatedUnion("success", [
  z.object({
    success: z.literal(true),
    data: z.object({ routes: z.array(XchainWithdrawRouteSchema) }),
  }),
  z.object({ success: z.literal(false), error: z.string() }),
]);

const WITHDRAW_ROUTE_KINDS = ["native", "intents"] as const;

type WithdrawRouteKind = (typeof WITHDRAW_ROUTE_KINDS)[number];

export type XchainWithdrawRoute = Omit<z.infer<typeof XchainWithdrawRouteSchema>, "kind"> & {
  kind: WithdrawRouteKind;
};

/**
 * Unlike the rest of the row, `kind` decides which code path runs, so a value this build cannot
 * execute is dropped rather than rendered. Filtering beats parsing it as an enum: a third kind
 * would otherwise fail the whole array and leave the modal with no cross-chain options at all,
 * instead of the ones it does understand.
 */
function isSupportedRoute(
  route: z.infer<typeof XchainWithdrawRouteSchema>,
): route is XchainWithdrawRoute {
  return (WITHDRAW_ROUTE_KINDS as readonly string[]).includes(route.kind);
}

export function xchainWithdrawRoutesQueryKey() {
  return ["xchainWithdrawRoutes"] as const;
}

// The allowlist only changes when the server is redeployed, so this is config, not data.
const ROUTES_STALE_TIME_MS = 10 * 60 * 1000;

/** Public endpoint — the allowlist is static server config, so no auth is threaded through. */
export function useXchainWithdrawRoutesQuery({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: xchainWithdrawRoutesQueryKey(),
    queryFn: async (): Promise<XchainWithdrawRoute[]> => {
      const response = await getRequest({
        schema: WithdrawRoutesResponseSchema,
        url: `${env.NEXT_PUBLIC_API_URL}/api/xchain/withdraw-routes`,
      });
      if (!response.data.success) {
        throw new Error(response.data.error);
      }
      return response.data.data.routes.filter(isSupportedRoute);
    },
    enabled,
    staleTime: ROUTES_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
